import { lazy, ComponentType } from 'react';
import { HomePage } from '../pages/HomePage';
import type { Product, WeightVariant } from '../components/ProductCard';

// Page component imports (lazy loaded for performance)
const CatalogPage = lazy(() =>
  import('../pages/CatalogPage').then(m => ({ default: m.CatalogPage }))
);
const ProductPage = lazy(() =>
  import('../pages/ProductPage').then(m => ({ default: m.ProductPage }))
);
const MenuPage = lazy(() =>
  import('../pages/MenuPage').then(m => ({ default: m.MenuPage }))
);
const RecipePage = lazy(() =>
  import('../pages/RecipePage').then(m => ({ default: m.RecipePage }))
);
const RecipesPage = lazy(() =>
  import('../pages/RecipesPage').then(m => ({ default: m.RecipesPage }))
);
const AccountSettingsPage = lazy(() =>
  import('../pages/AccountSettingsPage').then(m => ({
    default: m.AccountSettingsPage,
  }))
);
const ContactPage = lazy(() =>
  import('../pages/ContactPage').then(m => ({ default: m.ContactPage }))
);
const CollectionPage = lazy(() =>
  import('../pages/CollectionPage').then(m => ({ default: m.CollectionPage }))
);

// Development test components
const BackendRecipeTest = lazy(() =>
  import('../components/dev/BackendRecipeTest').then(m => ({ default: m.default }))
);

// Other pages (still in components, to be moved later)
const ShoppingCartPage = lazy(() =>
  import('../components/ShoppingCartPage').then(m => ({
    default: m.ShoppingCartPage,
  }))
);
const CheckoutPage = lazy(() =>
  import('../components/CheckoutPage').then(m => ({ default: m.CheckoutPage }))
);
const SearchResultsPage = lazy(() =>
  import('../components/SearchResultsPage').then(m => ({
    default: m.SearchResultsPage,
  }))
);

// Static/legal pages
const FAQPage = lazy(() =>
  import('../components/FAQPage').then(m => ({ default: m.FAQPage }))
);
const AboutUsPage = lazy(() =>
  import('../components/AboutUsPage').then(m => ({ default: m.AboutUsPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import('../components/PrivacyPolicyPage').then(m => ({
    default: m.PrivacyPolicyPage,
  }))
);
const TermsOfServicePage = lazy(() =>
  import('../components/TermsOfServicePage').then(m => ({
    default: m.TermsOfServicePage,
  }))
);
const CookiePolicyPage = lazy(() =>
  import('../components/CookiePolicyPage').then(m => ({
    default: m.CookiePolicyPage,
  }))
);

// Additional static pages
const ShippingReturnsPage = lazy(() =>
  import('../components/ShippingReturnsPage').then(m => ({
    default: m.ShippingReturnsPage,
  }))
);
const SizeGuidePage = lazy(() =>
  import('../components/SizeGuidePage').then(m => ({
    default: m.SizeGuidePage,
  }))
);
const TrackOrderPage = lazy(() =>
  import('../components/TrackOrderPage').then(m => ({
    default: m.TrackOrderPage,
  }))
);
const WholesalePage = lazy(() =>
  import('../components/WholesalePage').then(m => ({
    default: m.WholesalePage,
  }))
);
const GiftCardsPage = lazy(() =>
  import('../components/GiftCardsPage').then(m => ({
    default: m.GiftCardsPage,
  }))
);

// Route configuration interface
export interface RouteConfig {
  path: string;
  component: ComponentType<any>;
  requiresHeader?: boolean;
  requiresFooter?: boolean;
  seoTitle?: string;
  seoDescription?: string;
  isMainRoute?: boolean; // For language routing generation
}

// Main application routes (will be duplicated for each language)
export const mainRoutes: RouteConfig[] = [
  {
    path: '',
    component: HomePage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'homepage.meta.title',
    seoDescription: 'homepage.meta.description',
    isMainRoute: true,
  },
  {
    path: 'catalog',
    component: CatalogPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'catalog.meta.title',
    seoDescription: 'catalog.meta.description',
    isMainRoute: true,
  },
  {
    path: 'product/:handle',
    component: ProductPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'product.meta.title',
    seoDescription: 'product.meta.description',
    isMainRoute: true,
  },
  {
    path: 'recipes',
    component: RecipesPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'recipes.meta.title',
    seoDescription: 'recipes.meta.description',
    isMainRoute: true,
  },
  {
    path: 'recipe/:handle',
    component: RecipePage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'recipe.meta.title',
    seoDescription: 'recipe.meta.description',
    isMainRoute: true,
  },
  {
    path: 'menu',
    component: MenuPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'menu.meta.title',
    seoDescription: 'menu.meta.description',
    isMainRoute: true,
  },
  {
    path: 'account-settings',
    component: AccountSettingsPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'account.meta.title',
    seoDescription: 'account.meta.description',
    isMainRoute: true,
  },
  {
    path: 'contact',
    component: ContactPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'contact.meta.title',
    seoDescription: 'contact.meta.description',
    isMainRoute: true,
  },
  {
    path: 'collections/:handle',
    component: CollectionPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'collection.meta.title',
    seoDescription: 'collection.meta.description',
    isMainRoute: true,
  },
  {
    path: 'cart',
    component: ShoppingCartPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'cart.meta.title',
    seoDescription: 'cart.meta.description',
    isMainRoute: true,
  },
  {
    path: 'checkout',
    component: CheckoutPage,
    requiresHeader: false,
    requiresFooter: false,
    seoTitle: 'checkout.meta.title',
    seoDescription: 'checkout.meta.description',
    isMainRoute: true,
  },
];

// Utility routes (single language, not duplicated)
export const utilityRoutes: RouteConfig[] = [
  {
    path: 'search',
    component: SearchResultsPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'search.meta.title',
    seoDescription: 'search.meta.description',
  },
  // Development test route (only in development)
  ...(import.meta.env.DEV ? [{
    path: 'dev/backend-test',
    component: BackendRecipeTest,
    requiresHeader: false,
    requiresFooter: false,
    seoTitle: 'Backend Recipe API Test',
    seoDescription: 'Development testing page for Backend Recipe API',
  }] : []),
];

// Static/legal routes (single language)
export const staticRoutes: RouteConfig[] = [
  {
    path: 'faq',
    component: FAQPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'faq.meta.title',
    seoDescription: 'faq.meta.description',
  },
  {
    path: 'about',
    component: AboutUsPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'about.meta.title',
    seoDescription: 'about.meta.description',
  },
  {
    path: 'privacy-policy',
    component: PrivacyPolicyPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'privacy.meta.title',
    seoDescription: 'privacy.meta.description',
  },
  {
    path: 'terms-of-service',
    component: TermsOfServicePage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'terms.meta.title',
    seoDescription: 'terms.meta.description',
  },
  {
    path: 'cookie-policy',
    component: CookiePolicyPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'cookies.meta.title',
    seoDescription: 'cookies.meta.description',
  },
  {
    path: 'shipping-returns',
    component: ShippingReturnsPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'shipping.meta.title',
    seoDescription: 'shipping.meta.description',
  },
  {
    path: 'size-guide',
    component: SizeGuidePage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'size-guide.meta.title',
    seoDescription: 'size-guide.meta.description',
  },
  {
    path: 'track-order',
    component: TrackOrderPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'track-order.meta.title',
    seoDescription: 'track-order.meta.description',
  },
  {
    path: 'wholesale',
    component: WholesalePage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'wholesale.meta.title',
    seoDescription: 'wholesale.meta.description',
  },
  {
    path: 'gift-cards',
    component: GiftCardsPage,
    requiresHeader: true,
    requiresFooter: true,
    seoTitle: 'gift-cards.meta.title',
    seoDescription: 'gift-cards.meta.description',
  },
];

// All routes combined
export const allRoutes = [...mainRoutes, ...utilityRoutes, ...staticRoutes];

// Supported languages for routing
export const SUPPORTED_LANGUAGES = ['en', 'de', 'pl', 'uk'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Generate language-specific routes
export function generateLanguageRoutes(
  language: SupportedLanguage,
  routes: RouteConfig[]
) {
  return routes.map(route => ({
    ...route,
    path:
      language === 'en'
        ? route.path
        : `${language}/${route.path}`.replace('//', '/'),
  }));
}

// Generate all language combinations for main routes
export function generateAllLanguageRoutes() {
  const allLanguageRoutes: RouteConfig[] = [];

  SUPPORTED_LANGUAGES.forEach(lang => {
    const langRoutes = generateLanguageRoutes(lang, mainRoutes);
    allLanguageRoutes.push(...langRoutes);
  });

  return allLanguageRoutes;
}
